import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { LightTheme } from "../ThemeLight";

import { styles } from "./UserProfileTimeline.style";
import { IPlaceReport } from "../../../main";
import { IUserInfo } from "../../backend/cloud/UserTable";
import { IPlaceReportTable } from "../../backend/IPlaceReportTable";
import { watch } from "../internal/watch";
import { DataProvider } from "../../DataStore/DataProvider";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { eachLimit } from "async";
import { orderBy } from "lodash-es";
import { IPlaceTable } from "../../backend/IPlaceTable";
import { litIf } from "../LitUtils";
import { showPlaceReportEditorDialog } from "../PlaceReportEditorDialog/PlaceReportEditorDialog";
import { showDeletePlaceReportDialog } from "../PlaceView/PlaceView";

@customElement("ds-user-profile-timeline")
export class UserProfileTimeline extends BaseElement implements ChangeSetListener {
    static override styles = [styles, LightTheme];

    @property({ type: Object }) userInfo?: IUserInfo;
    @state() protected _userReports?: IPlaceReport[];

    private _userId?: string;
    private _placeReportTable = this._kernel.dataStore.getSource<IPlaceReportTable>("place-report");
    private _placesTable = this._kernel.dataStore.getSource<IPlaceTable>("places");

    @watch("userInfo")
    async onUserInfoUpdate() {
        if (this.userInfo && this.userInfo.owner) {
            this._userId = this.userInfo.owner;
            this._updateReports();
        }
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._placesTable.addListener(this, []);
        this._placeReportTable.addListener(this, []);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._placeReportTable.removeListener(this);
        this._placesTable.removeListener(this);
    }

    private async _updateReports() {
        if (this._userId) {
            this._userReports = await this._getUserReports(this._userId);
        }
    }

    private async _getUserReports(id: string): Promise<IPlaceReport[]> {
        const allReports = orderBy(
            Array.from(await this._placeReportTable.allOwnedBy(id)),
            (x) => x.date,
            "desc"
        );
        const reports: IPlaceReport[] = [];
        const havePlaces = new Map<string, boolean>();
        await eachLimit(allReports, 8, async (report) => {
            const placeId = report.placeReportsId;
            if (placeId) {
                let have = havePlaces.get(placeId);
                if (have === undefined) {
                    const place = await this._placesTable.read(placeId);
                    have = place !== undefined;
                    havePlaces.set(placeId, have);
                }
                if (have) {
                    reports?.push(report);
                }
            }
        });

        return reports;
    }

    onChangeSet(_provider: DataProvider, _changeSet: ChangeSet<any>): void {
        this._updateReports();
    }

    override render() {
        return html` ${litIf(
            this._userReports && this._userReports.length > 0,
            () => html`
                <lit-virtualizer
                    .items=${this._userReports ?? []}
                    .renderItem=${(x: IPlaceReport, _index: number) => html`
                        <ds-place-report
                            .report=${x}
                            showPlaceDetails
                            class="ds-box-shadow"
                            @ds-delete=${(e: CustomEvent) => {
                                showDeletePlaceReportDialog(e.detail.id, () => {});
                            }}
                            @ds-edit=${(e: CustomEvent) =>
                                showPlaceReportEditorDialog(
                                    {
                                        placeReport: e.detail.report,
                                        open: true,
                                        isTidal: e.detail.place.data.diveData?.isTidal ?? false,
                                    },
                                    () => {}
                                )}
                        ></ds-place-report>
                    `}
                ></lit-virtualizer>
            `
        )}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-user-profile-timeline": UserProfileTimeline;
    }
}
